import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { resolveImageUrl } from "@/utils/imageUrl";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ShowcaseDialog({
  open, onOpenChange, editingShowcase, showcaseForm, setShowcaseForm,
  handleShowcaseSubmit, saving,
}) {
  const { token } = useAuth();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle>{editingShowcase ? "Edit Showcase Product" : "Add Showcase Product"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleShowcaseSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input value={showcaseForm.title} onChange={e => setShowcaseForm(p => ({ ...p, title: e.target.value }))} placeholder="BECE Workbook 2026" required className="rounded-xl" data-testid="showcase-title-input" />
          </div>
          <div className="space-y-2">
            <Label>Description *</Label>
            <Textarea value={showcaseForm.description} onChange={e => setShowcaseForm(p => ({ ...p, description: e.target.value }))} placeholder="Comprehensive workbook covering all BECE subjects..." required rows={3} className="rounded-xl" data-testid="showcase-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Price (GHS, optional)</Label>
              <Input type="number" step="0.01" value={showcaseForm.price} onChange={e => setShowcaseForm(p => ({ ...p, price: e.target.value }))} placeholder="25.00" className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <select value={showcaseForm.category} onChange={e => setShowcaseForm(p => ({ ...p, category: e.target.value }))} className="w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                {["Books", "Workbooks", "Study Guides", "Past Questions", "Stationery", "General"].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Product Image *</Label>
            <div className="flex gap-3">
              <Input value={showcaseForm.image_url} onChange={e => setShowcaseForm(p => ({ ...p, image_url: e.target.value }))} placeholder="https://..." className="rounded-xl" data-testid="showcase-image-input" />
              {showcaseForm.image_url && (
                <img src={resolveImageUrl(showcaseForm.image_url)} alt="Preview" className="w-12 h-12 rounded-lg object-cover border" />
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              className="text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:rounded-xl file:border-0 file:bg-primary/10 file:text-primary file:font-medium file:cursor-pointer"
              data-testid="showcase-image-upload"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const fd = new FormData();
                fd.append("file", file);
                try {
                  const res = await axios.post(`${API}/admin/upload-image`, fd, {
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
                  });
                  setShowcaseForm(p => ({ ...p, image_url: res.data.url }));
                  toast.success("Image uploaded");
                } catch (err) { toast.error("Upload failed"); }
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={showcaseForm.is_published} onChange={e => setShowcaseForm(p => ({ ...p, is_published: e.target.checked }))} id="showcase-published" />
            <Label htmlFor="showcase-published">Published (visible to visitors)</Label>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 rounded-xl">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1 rounded-xl bg-primary">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingShowcase ? "Update" : "Create"} Product
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
